import React, { useState, useEffect, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { Send, Search, Lock, Check } from 'lucide-react';
import { getStoredUser, validatePin } from '../utils/helpers';
import axios from 'axios';
import { toast } from 'react-toastify';
import DashboardLayout from '../components/DashboardLayout';

const API_BASE = 'https://tradebot-k4nt.onrender.com/api';

const PeneTransfer = () => {
  const navigate = useNavigate();
  const [user, setUser] = useState(null);
  const [searchUsername, setSearchUsername] = useState('');
  const [searchResults, setSearchResults] = useState([]);
  const [showDropdown, setShowDropdown] = useState(false);
  const [selectedUser, setSelectedUser] = useState(null);
  const [amount, setAmount] = useState('');
  const [pin, setPin] = useState('');
  const [peneBalance, setPeneBalance] = useState(0);
  const [loading, setLoading] = useState(false);
  const [searching, setSearching] = useState(false);
  const dropdownRef = useRef(null);
  const inputRef = useRef(null);

  useEffect(() => {
    const userData = getStoredUser();
    if (!userData || !userData.telegramName) {
      navigate('/login');
      return;
    }
    setUser(userData);
    fetchPeneBalance(userData.telegramName);
  }, [navigate]);

  useEffect(() => {
    const debounceTimer = setTimeout(() => {
      if (searchUsername.length >= 2) {
        searchUsers();
      } else {
        setSearchResults([]);
        setShowDropdown(false);
      }
    }, 300);

    return () => clearTimeout(debounceTimer);
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [searchUsername]);

  useEffect(() => {
    const handleClickOutside = (event) => {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target) &&
          inputRef.current && !inputRef.current.contains(event.target)) {
        setShowDropdown(false);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  const fetchPeneBalance = async (username) => {
    try {
      const response = await axios.get(`${API_BASE}/payment/GetPeneAccountBalance-web/${username}`);
      if (response.data?.balance) {
        setPeneBalance(parseFloat(response.data.balance));
      }
    } catch (error) {
      console.error('Error fetching PENE balance:', error);
    }
  };

  const searchUsers = async () => {
    setSearching(true);
    try {
      const response = await axios.get(`${API_BASE}/payment/SearchUsers-web/${searchUsername}`);
      if (response.data?.mainUser) {
        const filtered = response.data.mainUser.filter(
          u => u.telegramName !== user.telegramName
        );
        setSearchResults(filtered);
        setShowDropdown(filtered.length > 0);
      }
    } catch (error) {
      console.error('Search error:', error);
    } finally {
      setSearching(false);
    }
  };

  const selectUser = (selectedUser) => {
    setSelectedUser(selectedUser);
    setSearchUsername(selectedUser.telegramName);
    setShowDropdown(false);
  };

  const handleTransfer = async (e) => {
    e.preventDefault();

    if (!selectedUser) {
      toast.error('Please select a recipient');
      return;
    }

    if (!amount || parseFloat(amount) <= 0) {
      toast.error('Please enter a valid amount');
      return;
    }

    if (parseFloat(amount) > peneBalance) {
      toast.error('Insufficient PENE balance');
      return;
    }

    if (!validatePin(pin)) {
      toast.error('Please enter a valid 6-digit PIN');
      return;
    }

    setLoading(true);

    try {
      const response = await axios.post(`${API_BASE}/payment/TransferPene-web`, {
        from: user.telegramName,
        to: selectedUser.telegramName,
        amount: parseFloat(amount),
        pin: pin
      });

      if (response.status === 200 || response.status === 201) {
        toast.success(`Successfully sent ${amount} PENE to ${selectedUser.telegramName}!`);
        fetchPeneBalance(user.telegramName);
        setSelectedUser(null);
        setSearchUsername('');
        setAmount('');
        setPin('');
      }
    } catch (error) {
      console.error('Transfer error:', error);
      toast.error(error.response?.data?.error || 'Transfer failed. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  if (!user) return null;

  return (
    <DashboardLayout>
      <div className="max-w-2xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        <div className="text-center mb-8">
          <div className="inline-flex items-center gap-2 mb-4">
            <div className="w-12 h-12 bg-gradient-to-br from-purple-600 to-pink-600 rounded-full flex items-center justify-center">
              <Send className="text-white" size={24} />
            </div>
          </div>
          <h1 className="text-3xl font-bold text-slate-900 mb-2">Transfer PENE</h1>
          <p className="text-slate-600">Send PENE to other users instantly</p>
        </div>

        <div className="bg-gradient-to-br from-purple-50 to-pink-50 border border-purple-200 rounded-xl p-4 mb-6">
          <p className="text-sm text-purple-700 mb-1">Your PENE Balance</p>
          <p className="text-3xl font-bold text-purple-900">{peneBalance.toLocaleString()} PENE</p>
        </div>

        <div className="bg-white rounded-2xl shadow-lg border border-slate-200 p-6">
          <form onSubmit={handleTransfer} className="space-y-5">
            <div className="relative">
              <label className="block text-sm font-medium text-slate-700 mb-2">
                Search Recipient
              </label>
              <div className="relative">
                <Search className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-400 z-10" size={20} />
                <input
                  ref={inputRef}
                  type="text"
                  placeholder="Search username..."
                  value={searchUsername}
                  onChange={(e) => setSearchUsername(e.target.value)}
                  className="w-full pl-11 pr-4 py-3 border-2 border-slate-300 rounded-xl focus:ring-2 focus:ring-purple-500 focus:border-transparent outline-none transition-all"
                  required
                />
                {searching && (
                  <div className="absolute right-3 top-1/2 -translate-y-1/2">
                    <div className="w-5 h-5 border-2 border-purple-600 border-t-transparent rounded-full animate-spin"></div>
                  </div>
                )}
              </div>

              {showDropdown && searchResults.length > 0 && (
                <div
                  ref={dropdownRef}
                  className="absolute top-full left-0 right-0 mt-2 bg-white border-2 border-slate-200 rounded-xl shadow-xl max-h-80 overflow-y-auto z-50"
                >
                  {searchResults.map((result, index) => (
                    <button
                      key={index}
                      type="button"
                      onClick={() => selectUser(result)}
                      className="w-full p-4 hover:bg-slate-50 transition-colors flex items-center gap-3 border-b border-slate-100 last:border-0"
                    >
                      <div className="w-10 h-10 rounded-full bg-gradient-to-br from-purple-500 to-pink-500 flex items-center justify-center text-white font-bold">
                        {result.telegramName.charAt(0).toUpperCase()}
                      </div>
                      <div className="flex-1 text-left">
                        <p className="font-semibold text-slate-900">{result.telegramName}</p>
                        {result.fullName && (
                          <p className="text-sm text-slate-600">{result.fullName}</p>
                        )}
                      </div>
                      {result.validated === 'verified' && (
                        <Check className="text-blue-500" size={20} />
                      )}
                    </button>
                  ))}
                </div>
              )}
            </div>

            {selectedUser && (
              <div className="p-4 bg-purple-50 border border-purple-200 rounded-xl">
                <div className="flex items-center gap-3">
                  <div className="w-12 h-12 rounded-full bg-gradient-to-br from-purple-500 to-pink-500 flex items-center justify-center text-white font-bold">
                    {selectedUser.telegramName.charAt(0).toUpperCase()}
                  </div>
                  <div className="flex-1">
                    <p className="font-semibold text-slate-900">Sending to:</p>
                    <p className="text-purple-700">{selectedUser.telegramName}</p>
                  </div>
                </div>
              </div>
            )}

            <div>
              <label className="block text-sm font-medium text-slate-700 mb-2">
                Amount (PENE)
              </label>
              <input
                type="number"
                step="0.01"
                placeholder="100"
                value={amount}
                onChange={(e) => setAmount(e.target.value)}
                className="w-full px-4 py-3 border-2 border-slate-300 rounded-xl focus:ring-2 focus:ring-purple-500 focus:border-transparent outline-none transition-all text-lg font-semibold"
                required
              />
            </div>

            <div>
              <label className="block text-sm font-medium text-slate-700 mb-2">
                Enter Your PIN
              </label>
              <div className="relative">
                <Lock className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-400" size={20} />
                <input
                  type="password"
                  placeholder="6-digit PIN"
                  value={pin}
                  onChange={(e) => setPin(e.target.value.replace(/\D/g, '').slice(0, 6))}
                  maxLength={6}
                  className="w-full pl-11 pr-4 py-3 border-2 border-slate-300 rounded-xl focus:ring-2 focus:ring-purple-500 focus:border-transparent outline-none transition-all text-center text-lg tracking-widest"
                  required
                />
              </div>
            </div>

            <button
              type="submit"
              disabled={loading || !selectedUser}
              className="w-full py-4 bg-gradient-to-r from-purple-600 to-pink-600 text-white font-bold rounded-xl hover:from-purple-700 hover:to-pink-700 transition-all shadow-lg disabled:opacity-50 disabled:cursor-not-allowed flex items-center justify-center gap-2"
            >
              {loading ? (
                <>
                  <div className="w-5 h-5 border-2 border-white border-t-transparent rounded-full animate-spin"></div>
                  Sending...
                </>
              ) : (
                <>
                  <Send size={20} />
                  Send PENE
                </>
              )}
            </button>
          </form>
        </div>

        <div className="mt-6 bg-blue-50 border border-blue-200 rounded-xl p-4">
          <p className="text-sm text-blue-900">
            <strong>💡 Note:</strong> All PENE transfers are instant and irreversible. Make sure you're sending to the correct user!
          </p>
        </div>
      </div>
    </DashboardLayout>
  );
};

export default PeneTransfer;
